<template>
  <v-container fluid>
    <!-- Two column stacks (map + swap-slot-A | stats + swap-slot-B) so the two
         columns size independently in two-column mode. On mobile the wrappers
         collapse to `display: contents` and the four cells reorder via the
         grid-areas (see styles). -->
    <div class="dashboard-grid" ref="grid" :style="gridStyle">
      <div class="col-left">
        <div class="h-col grid-map" ref="mapCell">
          <v-card elevation="0">
            <map-view :locations="strides" :loading="loading" />
          </v-card>
        </div>
        <div class="h-col grid-slot-a">
          <v-card elevation="0" class="swap-card">
            <button
              type="button"
              class="swap-btn"
              @click="toggleSwap"
              title="Swap leaderboard / item breakdown"
              aria-label="Swap leaderboard and item breakdown"
            >
              <v-icon size="18">swap_horiz</v-icon>
            </button>
            <items-breakdown
              v-if="!swapped"
              :stats="stats"
              :loading="statsLoading"
              :selection-stats="selectionStats"
              :selection-stats-loading="selectionStatsLoading"
            />
            <leaderboard v-else />
          </v-card>
        </div>
      </div>

      <!-- Drag to adjust the map/statistics split (desktop only). -->
      <div v-if="isDesktop" class="col-resizer">
        <div
          class="col-resizer__grip"
          @mousedown.prevent="startDrag"
          @dblclick="resetToDefault"
          title="Drag to resize (double-click to reset)"
        ></div>
      </div>

      <div class="col-right">
        <div class="h-col grid-stats">
          <v-card elevation="0" class="fill-card">
            <statistics
              :stats="stats"
              :loading="statsLoading"
              :selection-stats="selectionStats"
            />
          </v-card>
        </div>
        <!-- `grid-slot-b--fill` only when the leaderboard is the panel here: it
             stretches to match the left column (see the styles). Swapped, this
             slot holds the item breakdown, which keeps its own natural height. -->
        <div class="h-col grid-slot-b" :class="{ 'grid-slot-b--fill': !swapped }">
          <v-card elevation="0" class="swap-card">
            <button
              type="button"
              class="swap-btn"
              @click="toggleSwap"
              title="Swap leaderboard / item breakdown"
              aria-label="Swap leaderboard and item breakdown"
            >
              <v-icon size="18">swap_horiz</v-icon>
            </button>
            <leaderboard v-if="!swapped" />
            <items-breakdown
              v-else
              :stats="stats"
              :loading="statsLoading"
              :selection-stats="selectionStats"
              :selection-stats-loading="selectionStatsLoading"
            />
          </v-card>
        </div>
      </div>
    </div>

    <!-- While dragging, this overlay captures the mouse so the pointer can't be
         swallowed by the map iframe (which would break the drag). -->
    <div v-if="dragging" class="drag-overlay"></div>

    <!-- A shared link whose slug resolves to nothing. The dashboard behind it is
         unscoped and perfectly usable, so this is dismissible; the button just
         takes the URL back to the base so the dead link stops being copied on. -->
    <v-dialog
      v-model="notFoundOpen"
      max-width="380"
      @click:outside="dismissNotFound"
    >
      <v-card class="not-found">
        <!-- Two ways to get here, and they need different copy: the slug
             matched nothing (the link is dead), or the lookup itself failed (the
             link may be fine and retrying may work). Saying "not found" to
             someone whose network dropped sends them to fix a link that isn't
             broken. -->
        <v-card-title class="not-found__title">
          <template v-if="notFoundReason === 'error'">
            Couldn't load this
            {{ notFoundScope === "superteam" ? "superteam" : "team" }}
          </template>
          <template v-else>
            {{ notFoundScope === "superteam" ? "Superteam" : "Team" }} not found
          </template>
        </v-card-title>
        <v-card-text class="not-found__text">
          <template v-if="notFoundReason === 'error'">
            Something went wrong looking up
            <strong>{{ notFoundSlug }}</strong
            >. Check your connection and reload to try again — the dashboard
            below isn't filtered to it.
          </template>
          <template v-else>
            We couldn't find
            <strong>{{ notFoundSlug }}</strong
            >. The link may be out of date, or the
            {{ notFoundScope === "superteam" ? "superteam" : "team" }} may have
            been renamed.
          </template>
        </v-card-text>
        <!-- A dead slug is worth dropping out of the URL; a failed lookup is
             not. Retrying needs the slug the URL still holds, and $router.replace
             overwrites the history entry carrying it — for a shared link opened
             in a fresh tab there is nothing to go back to. So the error branch
             only closes the dialog. -->
        <v-card-actions class="not-found__actions">
          <v-spacer />
          <v-btn
            v-if="notFoundReason === 'error'"
            color="primary"
            depressed
            class="not-found__btn"
            @click="dismissNotFound"
          >
            Close
          </v-btn>
          <v-btn
            v-else
            color="primary"
            depressed
            class="not-found__btn"
            @click="backToDashboard"
          >
            Back to dashboard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import MapView from "../components/MapView.vue";
import Statistics from "../components/Statistics.vue";
import Leaderboard from "../components/Leaderboard.vue";
import ItemsBreakdown from "../components/ItemsBreakdown.vue";
import request from "../utils/request";
import { swrGet } from "../utils/swrCache";
import { countDistinct, jsonEqual } from "../utils/common";
import {
  buildFilterLocation,
  restoreFiltersFromQuery,
} from "../utils/urlFilters";
import { mapActions, mapState } from "vuex";
import { TABLET_WIDTH, TWO_COL_LANDSCAPE_MIN } from "../constants/breakpoints";

// Width at/above which the dashboard uses its two-column layout. Matches the
// $tablet-width boundary in styles/mixins.scss.
const DESKTOP_MIN = TABLET_WIDTH;
// Statistics column width, as a fraction of the row, with sane drag limits.
// The floor is a pixel width (converted to a fraction against the measured grid
// width at clamp time); STATS_MIN_FR is the fallback used before the grid is
// measurable (e.g. created()). The ceiling stays a fraction of the row.
const STATS_MIN_PX = 270;
const STATS_MIN_FR = 0.22;
const STATS_MAX_FR = 0.55;
// Default two-column split: the stats column gets 1/3 of the row, the map 2/3.
// A flat fraction (no longer derived from the map's rendered height) — the only
// adjustment is the STATS_MIN_PX floor kicking in on narrow grids (see
// clampFraction), which nudges stats above 1/3 there.
const STATS_DEFAULT_FR = 1 / 3;
const STORAGE_KEY = "dashboard.statsFraction";
// A slug lookup that failed outright, as distinct from one that cleanly matched
// nothing (null). The two need different copy and, for the child half of an
// /org/:slug/:teamSlug link, different handling — see restoreScopeFromSlugs.
const LOOKUP_FAILED = Symbol("lookup failed");
// Whether the leaderboard and item-breakdown panels have been swapped. Persisted
// like statsFraction so the chosen arrangement survives reloads.
const SWAP_STORAGE_KEY = "dashboard.panelsSwapped";

// Two-column map height as a fraction of the available content height — the space
// below the fixed header (and outside the v-main offset / container padding), NOT
// the viewport. updateMapHeight() measures that space (window.innerHeight minus
// the grid's top edge) and feeds MAP_TWO_COL_HEIGHT_FRACTION × it to MapView via
// the --map-two-col-height CSS var (see gridStyle). MapView falls back to its own
// default if unset; gridStyle falls back to 60lvh until the first measurement.
const MAP_TWO_COL_HEIGHT_FRACTION = 0.6;

// A path segment as a slug the lookup can match: slugs are lowercase by contract,
// so a link typed in mixed case still resolves. An absent segment is no slug.
function normaliseSlug(value) {
  if (value == null) return "";
  return String(value).trim().toLowerCase();
}

export default {
  name: "Home",
  components: {
    MapView,
    Statistics,
    Leaderboard,
    ItemsBreakdown,
  },
  data() {
    return {
      summary: {
        total_users: 0,
        total_countries: 0,
        countries: [],
      },
      strides: [],
      // Stats for the Statistics + ItemsBreakdown panels. Fetched once here and
      // passed to both, so /dashboards/strides_stats isn't requested twice.
      stats: {
        total_distance: 0,
        total_items: [],
        total_time_in_minutes: 0,
        total_strides: 0,
      },
      statsLoading: false,
      // Weight + per-item-type breakdown for the active map selection, fetched
      // from POST /dashboards/strides_stats and shared with both the Statistics
      // panel (Weight metric) and the ItemsBreakdown panel (pie + list). Fetched
      // here (once) so the two panels don't each request it. null = none/pending.
      selectionStats: null,
      selectionStatsLoading: false,
      // Leaderboard <-> item-breakdown panel positions swapped (see toggleSwap).
      swapped: false,
      loading: false,
      isDesktop: false,
      // dragArmed: listeners attached after mousedown. dragging: the mouse has
      // actually moved, so the capture overlay is shown. Splitting them lets a
      // plain click / double-click on the handle avoid spawning the overlay
      // (which would otherwise swallow the dblclick).
      dragArmed: false,
      dragging: false,
      statsFraction: STATS_DEFAULT_FR,
      // True once the user has dragged (or restored a saved split): then we stop
      // recomputing the default fraction on resize and honour their choice.
      hasCustomFraction: false,
      // Measured two-column map height in px (MAP_TWO_COL_HEIGHT_FRACTION of the
      // content height below the header). null until first measured — gridStyle
      // falls back to 60lvh then. See updateMapHeight().
      mapHeightPx: null,
      // The slug from the URL that resolved to nothing, and which namespace the
      // route put it in ("team" | "superteam"). Non-null = the not-found dialog
      // is up. Only set for the leading segment: an unresolvable child under a
      // resolvable parent still leaves a valid superteam view on screen.
      notFoundSlug: null,
      notFoundScope: null,
      // Why the scope couldn't be restored: "missing" (the slug matched nothing)
      // or "error" (the lookup failed). Only the dialog's wording reads it.
      notFoundReason: null,
    };
  },
  computed: {
    ...mapState([
      "startDay",
      "endDay",
      "country",
      "team",
      "teamName",
      "teamSlug",
      "superteam",
      "superteamName",
      "superteamSlug",
      "userId",
      "userName",
      "selection",
    ]),
    // v-model for the not-found dialog. The slug is the state; this is just its
    // boolean face, with a setter so Escape / the overlay clear it too.
    notFoundOpen: {
      get() {
        return this.notFoundSlug != null;
      },
      set(open) {
        if (!open) this.dismissNotFound();
      },
    },
    filterConditions() {
      return [
        this.startDay,
        this.endDay,
        this.country,
        this.team,
        this.superteam,
        this.userId,
      ].join();
    },
    // The location ({ path, query }) mirroring the active filters — kept in sync
    // by the urlLocation watcher below. See src/utils/urlFilters.js.
    urlLocation() {
      return buildFilterLocation({
        startDay: this.startDay,
        endDay: this.endDay,
        country: this.country,
        team: this.team,
        teamName: this.teamName,
        teamSlug: this.teamSlug,
        superteam: this.superteam,
        superteamName: this.superteamName,
        superteamSlug: this.superteamSlug,
        userId: this.userId,
      });
    },
    // Only drive the columns in two-column mode (see isDesktop); otherwise the
    // CSS single-column template (map → stats → board) takes over. fr units on
    // BOTH columns mean they shrink/grow together as the window resizes
    // (restoring the previous "both shrink together" behaviour); the middle 0px
    // track holds the handle.
    gridStyle() {
      // Always expose the map-height var, even in single-column mode (where the
      // two-col CSS ignores it): emitting it unconditionally means the map
      // renders at the two-col height from the first paint instead of
      // transitioning down from MapView's 70lvh fallback once isDesktop flips
      // true.
      const style = {
        "--map-two-col-height":
          this.mapHeightPx != null
            ? `${this.mapHeightPx}px`
            : `${MAP_TWO_COL_HEIGHT_FRACTION * 100}lvh`,
      };
      if (this.isDesktop) {
        const stats = this.statsFraction;
        const map = 1 - stats;
        style.gridTemplateColumns = `minmax(0, ${map}fr) 0px minmax(0, ${stats}fr)`;
      }
      return style;
    },
  },
  watch: {
    filterConditions() {
      this.loadStrides();
      this.loadStats();
    },
    // Fetch the selection's weight + item breakdown when the map selection
    // changes (cleared → drop it). Shared by the Statistics + ItemsBreakdown
    // panels via the selectionStats prop.
    selection(newSelection) {
      this.selectionStats = null;
      if (newSelection) this.loadSelectionStats(newSelection);
    },
    // Keep the URL in sync with the active filters (shareable/bookmarkable
    // links). Both path and query are written: a scope with a known slug lives in
    // the path, so clearing it has to take the path back to "/" too.
    // router.replace (not push) so filter tweaks don't spam browser history.
    // Swallow a same-route "NavigationDuplicated" rejection — it's an expected
    // no-op, not an error.
    urlLocation({ path, query }) {
      this.$router.replace({ path, query }).catch(() => {});
    },
  },
  created() {
    restoreFiltersFromQuery(this.$route.query, {
      selectUser: this.selectUser,
      selectMember: this.selectMember,
      selectSuperteam: this.selectSuperteam,
      selectTeam: this.selectTeam,
      drillTeam: this.drillTeam,
      updateStartDay: this.updateStartDay,
      updateEndDay: this.updateEndDay,
      updateCountry: this.updateCountry,
    });
    // A slug subpath (/temasek-polytechnic, /org/green-school-network,
    // /org/green-school-network/temasek-polytechnic) is the canonical form of a
    // scoped link. Only honour it when no explicit team/superteam query param
    // already scoped the view above — those win, so an older
    // ?team=123&teamName=… link isn't second-guessed by whatever the path says.
    // A `?user=` doesn't block it: that's the member half of /<team-slug>?user=,
    // which restoreScopeFromSlugs re-applies over the resolved team.
    // The route name says which namespace the slug is in (see src/router).
    const { team, superteam } = this.$route.query;
    // `params` is absent on a route with no path params, so read it defensively.
    const { slug, teamSlug } = this.$route.params || {};
    if (!team && !superteam && slug) {
      const isSuperteam =
        this.$route.name === "SuperteamScope" ||
        this.$route.name === "SuperteamTeamScope";
      this.restoreScopeFromSlugs(
        slug,
        isSuperteam ? "superteam" : "team",
        isSuperteam ? teamSlug : null
      );
    }
    const saved = parseFloat(this.readSavedFraction());
    if (!isNaN(saved)) {
      this.statsFraction = this.clampFraction(saved);
      this.hasCustomFraction = true;
    }
    try {
      this.swapped = window.localStorage.getItem(SWAP_STORAGE_KEY) === "1";
    } catch (e) {
      // ignore (e.g. storage disabled)
    }
  },
  mounted() {
    this.loadSummary();
    this.loadStrides();
    this.loadStats();

    this.onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const wasDesktop = this.isDesktop;
      // Wide viewport, OR landscape (wide-but-short) above the landscape floor.
      // Use w >= h (not w > h) to match the CSS `two-col` mixin's
      // `min-aspect-ratio: 1/1`, so an exactly-square viewport agrees on both
      // sides (else CSS goes two-column but the resizer never renders).
      this.isDesktop =
        w >= DESKTOP_MIN || (w >= h && w >= TWO_COL_LANDSCAPE_MIN);
      // Map height tracks the available content height, so re-measure on every
      // resize (cheap; the value only changes when the viewport or header does).
      this.updateMapHeight();
      // Set the default split only when we (re)enter two-column mode: on first
      // layout and on a breakpoint flip. On plain resizes within a mode we leave
      // statsFraction untouched so the fr-based columns scale proportionally (map
      // and stats shrink together) — matching the behaviour once the user has
      // dragged the handle.
      if (
        !this.hasCustomFraction &&
        this.isDesktop &&
        this.isDesktop !== wasDesktop
      ) {
        this.updateDefaultFraction();
      }
    };
    this.onResize();
    window.addEventListener("resize", this.onResize);
    // The header measures itself asynchronously (App.vue) and can reflow on font
    // load, shifting the grid's top edge without a resize event. Re-measure the
    // map height once that has settled so the initial 60%-of-content is correct.
    this.$nextTick(() => this.updateMapHeight());
    if (window.document.fonts && window.document.fonts.ready) {
      window.document.fonts.ready.then(() => this.updateMapHeight());
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    this.stopDrag();
  },
  methods: {
    ...mapActions([
      "updateTotalUsers",
      "updateTotalCountries",
      "updateCountries",
      "updateStridesUsers",
      "updateStridesCountries",
      "selectUser",
      "selectMember",
      "updateUserName",
      "selectSuperteam",
      "selectTeam",
      "drillTeam",
      "updateStartDay",
      "updateEndDay",
      "updateCountry",
    ]),
    // Resolve a slug subpath to real team/superteam ids and apply it as a filter.
    // `scope` is "team" or "superteam" — the namespace the URL put the slug in
    // (/:slug vs /org/:slug), not something inferred from the response.
    // `childSlug` is the second segment of /org/:slug/:teamSlug: the child team a
    // superteam is drilled into, always looked up in the team namespace.
    //
    // The resolved scope carries its slug into the store, so the URL the watcher
    // writes back is the same slug path the link already had — no rewrite to ids.
    //
    // Slugs are lowercase and hyphenated, unique within their own namespace, and
    // are looked up server-side via `/dashboards/teams?slug=&type=`. A slug the
    // backend doesn't know matches nothing and the dashboard opens unscoped:
    // inert rather than wrong, since no row can claim a slug it doesn't carry.
    //
    // The params arrive already percent-decoded (vue-router decodes path params
    // itself), so decoding again here would corrupt a slug containing a literal
    // "%" and gains nothing for the rest.
    async restoreScopeFromSlugs(rawSlug, scope, rawChildSlug) {
      const slug = normaliseSlug(rawSlug);
      if (!slug) return;
      const childSlug =
        scope === "superteam" ? normaliseSlug(rawChildSlug) : null;
      // A `?user=` on a slug path is a member of the team the path names, but the
      // team actions below clear any individual. Hold the one the query already
      // restored and re-apply it once the scope lands.
      const member =
        this.userId != null ? { id: this.userId, name: this.userName } : null;
      try {
        // Both lookups in flight together; applied in order below, since
        // selectSuperteam clears any team. Each catches its own failure rather
        // than letting Promise.all reject: a rejection here would skip the
        // selectSuperteam below, so a child that failed to load would leave the
        // superteam unapplied and be reported as the superteam failing — the
        // opposite of the rule stated at the drill call.
        const [match, childMatch] = await Promise.all([
          this.lookupSlug(slug, scope).catch(() => LOOKUP_FAILED),
          childSlug
            ? this.lookupSlug(childSlug, "team").catch(() => LOOKUP_FAILED)
            : null,
        ]);
        if (match === LOOKUP_FAILED) {
          // The lookup failed outright — network, 5xx, a malformed response. The
          // scope is just as unresolved as it is on a no-match, so it gets the
          // same treatment: say so. Swallowing this into a console.log left the
          // global view showing under a team-shaped URL, which is precisely what
          // the no-match branch below exists to prevent.
          this.showNotFound(rawSlug, scope, "error");
          return;
        }
        if (!match) {
          // The link names something that doesn't exist. Leave the dashboard
          // unscoped (and the URL alone — the watcher only rewrites it on a
          // filter change) and say so, rather than silently showing the global
          // view under a team-shaped URL.
          this.showNotFound(rawSlug, scope, "missing");
          return;
        }
        if (scope === "superteam") {
          this.selectSuperteam({ id: match.id, name: match.name, slug });
          // An unresolvable child leaves the superteam scope alone — the parent
          // half of the link is still a valid view. A child that failed to load
          // says so, naming the child: the parent IS filtered, so the parent's
          // copy would be wrong on both counts. A child that cleanly matched
          // nothing stays silent, as it always has.
          if (childMatch === LOOKUP_FAILED) {
            this.showNotFound(rawChildSlug, "team", "error");
          } else if (childMatch) {
            this.drillTeam({
              id: childMatch.id,
              name: childMatch.name,
              slug: childSlug,
            });
          }
        } else {
          this.selectTeam({ id: match.id, name: match.name, slug });
        }
        if (member) this.selectMember(member);
      } catch (error) {
        // Neither lookup can land here any more; this catches anything else the
        // block can throw, and still says so rather than leaving a team-shaped
        // URL over global data.
        console.log(error);
        this.showNotFound(rawSlug, scope, "error");
      }
    },
    showNotFound(slug, scope, reason) {
      this.notFoundSlug = slug;
      this.notFoundScope = scope;
      this.notFoundReason = reason;
    },
    dismissNotFound() {
      this.notFoundSlug = null;
      this.notFoundScope = null;
      this.notFoundReason = null;
    },
    // Drop the dead slug out of the URL. Nothing is scoped, so this is a pure
    // route change — no filter state to clear, and the urlLocation watcher has
    // nothing to write back over it.
    backToDashboard() {
      this.dismissNotFound();
      this.$router.replace({ path: "/", query: {} }).catch(() => {});
    },
    // One slug → row lookup in a single namespace. Returns null when nothing
    // matches.
    async lookupSlug(slug, type) {
      const { fresh } = swrGet("/dashboards/teams", {
        params: { slug, type },
      });
      const data = await fresh;
      const rows = (data && data.teams) || [];
      // Match on the row's own slug and type rather than trusting the endpoint to
      // have filtered: a backend that ignores unknown params would otherwise hand
      // back an unrelated first row.
      return (
        rows.find(
          (t) =>
            t.slug === slug &&
            (type === "superteam"
              ? t.type === "superteam"
              : t.type !== "superteam")
        ) || null
      );
    },
    clampFraction(f, gridWidth) {
      // Convert the px floor to a fraction using the measured grid width; fall
      // back to STATS_MIN_FR when the width isn't known yet (e.g. created()).
      // Cap the floor at STATS_MAX_FR: on a grid too narrow to honour both the
      // px floor and the max fraction (grid < STATS_MIN_PX / STATS_MAX_FR,
      // ~491px), give stats the widest it's allowed rather than letting the
      // outer clamp collapse it back below the floor.
      const minFr = Math.min(
        STATS_MAX_FR,
        gridWidth ? STATS_MIN_PX / gridWidth : STATS_MIN_FR
      );
      return Math.max(minFr, Math.min(STATS_MAX_FR, f));
    },
    // Set the default split to a flat STATS_DEFAULT_FR (1/3 stats, 2/3 map).
    // Measured grid width is passed to clampFraction only so the STATS_MIN_PX
    // floor can be honoured on narrow grids. Deferred a frame so the grid has
    // settled after a breakpoint flip. No-op once the user has dragged.
    updateDefaultFraction() {
      window.requestAnimationFrame(() => {
        const grid = this.$refs.grid;
        if (!grid || this.hasCustomFraction) return;
        const gridWidth = grid.clientWidth;
        if (!gridWidth) return;
        this.statsFraction = this.clampFraction(STATS_DEFAULT_FR, gridWidth);
      });
    },
    // Measure the map's two-column height: MAP_TWO_COL_HEIGHT_FRACTION of the
    // content height below the header. The grid's top edge sits below the fixed
    // header, the v-main offset and the container's top padding, so
    // (innerHeight − grid top) is the space actually available to the dashboard.
    // Add scrollY to convert the viewport-relative rect into a document-relative
    // top, so the measurement is the same whether or not the page is scrolled.
    // Without it, a reload with a restored scroll position (or any re-measure
    // taken mid-page) inflates avail by the scroll offset and the map grows.
    // The header's slide is a transform, and v-main's offset comes from the
    // header height prop, so the grid's document top doesn't move on scroll.
    updateMapHeight() {
      const grid = this.$refs.grid;
      if (!grid) return;
      const top =
        grid.getBoundingClientRect().top +
        (window.scrollY || window.pageYOffset || 0);
      const avail = window.innerHeight - top;
      if (avail > 0) {
        this.mapHeightPx = Math.round(MAP_TWO_COL_HEIGHT_FRACTION * avail);
      }
    },
    readSavedFraction() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (e) {
        return null;
      }
    },
    startDrag() {
      this.dragArmed = true;
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      const grid = this.$refs.grid;
      if (!grid) return;
      // First real movement: now show the capture overlay.
      this.dragging = true;
      const rect = grid.getBoundingClientRect();
      // Stats is the right column: its width is the distance from the cursor to
      // the grid's right edge.
      const statsW = rect.right - e.clientX;
      this.statsFraction = this.clampFraction(statsW / rect.width, rect.width);
    },
    stopDrag() {
      if (!this.dragArmed) return;
      const moved = this.dragging;
      this.dragArmed = false;
      this.dragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
      // A click without movement isn't a real resize — don't lock in a custom
      // split or persist anything (leaves double-click / defaults intact).
      if (!moved) return;
      this.hasCustomFraction = true;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(this.statsFraction));
      } catch (e) {
        // ignore (e.g. storage disabled)
      }
    },
    // Double-click the handle: forget the user's custom split and recompute the
    // measured rectangular-map default (clearing the saved value too).
    resetToDefault() {
      this.hasCustomFraction = false;
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // ignore (e.g. storage disabled)
      }
      this.updateDefaultFraction();
    },
    // Flip the leaderboard / item-breakdown panel positions and persist the
    // choice (mirrors the statsFraction persistence).
    toggleSwap() {
      this.swapped = !this.swapped;
      try {
        window.localStorage.setItem(SWAP_STORAGE_KEY, this.swapped ? "1" : "0");
      } catch (e) {
        // ignore (e.g. storage disabled)
      }
    },
    async loadSummary() {
      try {
        const { data } = await request.get("/dashboards/summary");
        this.summary = data;
        this.updateTotalUsers(data.total_users);
        this.updateTotalCountries(data.total_countries);
        this.updateCountries(data.countries || []);
      } catch (error) {
        console.error("Error fetching summary data:", error);
      }
    },
    // The item-type stats feeding the Statistics + ItemsBreakdown panels. Fetched
    // here (once) rather than in each panel, so the two share a single request.
    // Served stale-while-revalidate: a cached payload for this filter renders
    // immediately (no spinner) while a background refresh runs. The key check
    // replaces the previous AbortController — rapid filter changes can't apply
    // out of order, and every completed response still warms the cache.
    async loadStats() {
      const params = {
        start_date: this.startDay,
        end_date: this.endDay,
        country: this.country,
        team: this.team,
        // Send superteam only when NOT drilled into a child team: superteam-only
        // triggers the mock's aggregation; a drilled team is a plain team request.
        // (axios drops null/undefined params, so a real backend never sees it.)
        superteam: this.team ? undefined : this.superteam || undefined,
        user_id: this.userId,
      };
      const { key, cached, fresh } = swrGet("/dashboards/strides_stats", {
        params,
      });
      this.statsKey = key;
      if (cached) this.stats = cached.stats;
      this.statsLoading = !cached;
      try {
        const data = await fresh;
        if (this.statsKey !== key) return; // a newer filter owns the panel
        // Skip reassigning when the revalidation changed nothing.
        if (!cached || !jsonEqual(data, cached)) this.stats = data.stats;
      } catch (error) {
        if (this.statsKey === key) {
          console.error("Error fetching stats data:", error);
        }
      } finally {
        if (this.statsKey === key) this.statsLoading = false;
      }
    },
    // Write a strides payload into component + store state. Split out of
    // loadStrides so the SWR path can apply a cached payload and, later, the
    // revalidated one through the same code.
    applyStrides(data) {
      // Default to [] so a malformed/empty response can't break the map render
      // (MapView iterates this.strides) or the countDistinct fallback below.
      this.strides = data.strides || [];
      this.applyUserName(data);
      // The backend only populates summary.strides_users/countries for
      // team-filtered requests; for country/date-only filters they come back
      // as 0. Only trust the backend figure when it's a positive number;
      // otherwise fall back to counting distinct user_names / countries from
      // the strides themselves so the headline figures still reflect the
      // filter.
      const summary = data.summary || {};
      const stridesUsers =
        summary.strides_users > 0
          ? summary.strides_users
          : countDistinct(data.strides, "user_name");
      const stridesCountries =
        summary.strides_countries > 0
          ? summary.strides_countries
          : countDistinct(data.strides, "country");
      this.updateStridesUsers(stridesUsers);
      this.updateStridesCountries(stridesCountries);
    },
    // Name the individual filter from the strides response, so it doesn't have to
    // travel in the URL (see utils/urlFilters.js — a strider's name is PII, and a
    // URL reaches analytics, history and Referer headers).
    //
    // `user` is the echo of the queried strider, returned whenever `user_id` was
    // sent — including for a window with no strides, which is the case the
    // per-stride `user_name` fallback can't cover. Until a backend serves it, that
    // fallback names every scope whose filtered window holds at least one stride;
    // an empty one shows the generic "Individual" label instead.
    applyUserName(data) {
      if (!this.userId) return;
      const echoed = data.user && data.user.name;
      const fromStride = (data.strides || []).find((s) => s.user_name);
      const name = echoed || (fromStride && fromStride.user_name) || null;
      // Only when we learned something: a nameless response must not wipe a name
      // an older ?userName= link (or the row that was clicked) already supplied.
      if (name && name !== this.userName) this.updateUserName(name);
    },
    // Strides feed the map markers (and the strides_users/countries totals).
    // Stale-while-revalidate like loadStats. An unchanged revalidation is NOT
    // reapplied: reassigning `strides` rebuilds every map marker, clears the
    // cluster selection and re-frames the map, so identical data must leave
    // the component state untouched. (A cache hit re-serves the same array
    // reference, so the MapView `locations` watcher doesn't fire either.)
    async loadStrides() {
      const params = {
        start_date: this.startDay,
        end_date: this.endDay,
        country: this.country,
        team: this.team,
        // See loadStats: superteam only when not drilled into a child team.
        superteam: this.team ? undefined : this.superteam || undefined,
        user_id: this.userId,
      };
      const { key, cached, fresh } = swrGet("/dashboards/strides", { params });
      this.stridesKey = key;
      if (cached) this.applyStrides(cached);
      this.loading = !cached;
      try {
        const data = await fresh;
        if (this.stridesKey !== key) return; // a newer filter owns the map
        if (!cached || !jsonEqual(data, cached)) this.applyStrides(data);
      } catch (error) {
        if (this.stridesKey === key) console.error(error);
      } finally {
        if (this.stridesKey === key) this.loading = false;
      }
    },
    // Weight + per-item-type breakdown for a map selection (cluster/cleanup).
    // POST (not GET) so a large cluster's stride-id list can't overflow the URL.
    // Guards against a stale selection: if the user picks another selection
    // while this is in flight, the result is dropped.
    async loadSelectionStats(selection) {
      const strideIds = (selection && selection.strideIds) || [];
      if (!strideIds.length) return;
      this.selectionStatsLoading = true;
      try {
        const { data } = await request.post("/dashboards/strides_stats", {
          stride_ids: strideIds,
        });
        if (this.selection !== selection) return; // superseded selection
        this.selectionStats = data.stats;
      } catch (error) {
        if (this.selection === selection) {
          console.error("Error fetching selection breakdown:", error);
        }
      } finally {
        if (this.selection === selection) this.selectionStatsLoading = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/styles/mixins.scss";
@import "@/styles/variables.scss";
.container {
  padding: 15px;

  @include mobile {
    padding: 0;
  }
}
::v-deep {
  .v-card {
    border-radius: 16px !important;
  }
}

.not-found {
  background-color: $color-panel;
  padding: 4px;
  // v-dialog detaches its content to [data-app], so it falls outside the
  // ::v-deep .v-card rule above — set the shared radius here too.
  border-radius: 16px;

  &__title {
    font-size: 1.05rem;
    font-weight: 600;
    color: $color-text-primary;
    word-break: normal;
  }

  &__text {
    color: $color-text-muted;
    // Long slugs shouldn't push the dialog wide.
    overflow-wrap: anywhere;
  }

  &__actions {
    padding-top: 0;
  }

  // The button sits on `primary` (lime), whose readable foreground is the flat
  // dark green, not the theme's text colour. See colour-palette.md.
  &__btn {
    color: $color-on-primary !important;
  }
}

// Narrow/portrait: one column, in order map → statistics → swap-slot-A →
// swap-slot-B (leaderboard / item breakdown, whichever the swap places there).
// The two column wrappers collapse to `display: contents` so their cells become
// direct grid items and follow the single-column grid-areas below rather than
// DOM order. When there's enough horizontal room (wide viewport, or a landscape
// phone — see the `two-col` mixin): two columns, each an independent flex stack
// (map + slot-A on the left, stats + slot-B on the right), with a drag handle in
// the thin middle track. Column widths come from the inline `gridStyle`
// (fr-based, so both shrink together); the values here are the pre-mount fallback.

// Shortest the leaderboard is allowed to get while it stretches to match the
// left column (see .grid-slot-b--fill).
$leaderboard-min-height: 300px;

.dashboard-grid {
  display: grid;
  // minmax(0, 1fr), not plain 1fr: a bare `1fr` track is `minmax(auto, 1fr)`,
  // whose auto minimum grows to the content's min-content. A single-line
  // (flex-wrap: nowrap) leaderboard row has a large min-content (the sum of its
  // cells), so a `1fr` track would stretch past the viewport and make the whole
  // mobile page overflow/zoom. Capping the minimum at 0 lets the column stay
  // within the screen and the row clip instead — same guard the two-col layout
  // below already uses.
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "map"
    "stats"
    "slota"
    "slotb";

  .col-left,
  .col-right {
    display: contents;
  }

  @include two-col {
    grid-template-columns: minmax(0, 1fr) 0px minmax(0, 0.32fr);
    // No grid-areas in two-column mode: the two wrappers are real grid items
    // (auto-placed left / handle / right) and stack their own cells with flex,
    // so each column sizes independently to its content.
    grid-template-areas: none;

    .col-left,
    .col-right {
      display: flex;
      flex-direction: column;
      min-width: 0;
    }

    // Only when the leaderboard is the panel in this slot (the template adds the
    // class from `!swapped`). Both columns are grid items in the same row, so
    // both are stretched to whichever is taller; the left column (map + item
    // breakdown) is left content-sized, so it is the one that sets that height,
    // and the leaderboard then absorbs the space the statistics panel above it
    // didn't use rather than stopping short at its own 530px.
    //
    // Deliberately leaderboard-only: swapped, this slot holds the item breakdown
    // and gets none of this, so that panel keeps the natural height it has in
    // every other position.
    //
    // `flex: 1 1 0` — not `1 1 auto` — is what keeps the leaderboard out of the
    // column's own height: with a zero flex-basis its content no longer adds to
    // what the column asks the grid row for, so a long list can't push the row
    // taller and then fill the extra space it just created.
    // $leaderboard-min-height is the floor: below it the panel stops shrinking
    // and makes its column taller instead (the left column then gets the
    // leftover space as an empty gap). It exists so a short left column — a
    // landscape phone, say — can't squeeze the list down to a couple of rows.
    .col-right > .grid-slot-b--fill {
      flex: 1 1 0;
      min-height: $leaderboard-min-height;

      // Read by Leaderboard.vue's `.lb-wrap` (custom properties inherit, so the
      // scoped-style boundary doesn't matter).
      --lb-height: 100%;

      // The card is the panel's parent, so it has to pass the height through.
      > .swap-card {
        height: 100%;
      }
    }
  }
}
.grid-map {
  grid-area: map;
}
.grid-stats {
  grid-area: stats;
}
.grid-slot-a {
  grid-area: slota;
}
.grid-slot-b {
  grid-area: slotb;
}

// The handle floats in the gap between the two columns; in two-column mode it's
// auto-placed as the middle grid item (the 0-width track).
.col-resizer {
  position: relative;
  width: 0;
  align-self: stretch;
  z-index: 2;

  // The grip is mouse-driven, so hide it on touch / coarse-pointer devices
  // (incl. landscape phones, where two-column mode would otherwise show a
  // non-draggable, confusing grip). The measured default split still applies.
  @media (hover: none), (pointer: coarse) {
    display: none;
  }
}
.col-resizer__grip {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -10px; // centre the 20px hit area on the gap between the cards
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: col-resize;

  &::before {
    content: "";
    width: 4px;
    height: 48px;
    border-radius: 2px;
    background: rgba(0, 0, 0, 0.15);
    transition: background 0.15s ease;
  }
  &:hover::before {
    background: rgba(0, 0, 0, 0.35);
  }
}
.theme--dark .col-resizer__grip::before {
  background: rgba(255, 255, 255, 0.15);
}
.theme--dark .col-resizer__grip:hover::before {
  background: rgba(255, 255, 255, 0.3);
}

// Full-window shield active only during a drag, so the cursor stays captured
// even over the map iframe.
.drag-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  cursor: col-resize;
}

.fill-card {
  background-color: $color-panel;
  height: 100%;
}
.h-col {
  padding: 10px !important;

  @include mobile {
    padding: 6px !important;
  }
}

// Swap slots host the leaderboard / item-breakdown panels; position relative so
// the corner swap button anchors to the card.
.swap-card {
  background-color: $color-panel;
  position: relative;
}
// Small, muted corner button that swaps the two panels' positions. Quiet by
// default (matches the resize grip's understated look), warming on hover.
.swap-btn {
  position: absolute;
  top: 1px;
  left: 1px;
  z-index: 4;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 16px;
  background: transparent;
  color: rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  ::v-deep .v-icon {
    color: inherit;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: rgba(0, 0, 0, 0.55);
  }
}
.theme--dark .swap-btn {
  color: rgba(255, 255, 255, 0.3);
}
.theme--dark .swap-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
}
</style>
